import { useId, useState, type MouseEvent, type ReactNode } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SelectProps,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

export type TriState = boolean | null;

/**
 * Valore del `Select` (MUI `MenuItem` accetta solo string | number).
 * `''` = non scelto (null), `'true'`/`'false'` = SI / NO come boolean serializzati.
 */
export type TriSelectValue = '' | 'true' | 'false';

function toSelectValue(value: TriState | undefined): TriSelectValue {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

export interface TriStateCheckboxProps
  extends Omit<
    SelectProps<TriSelectValue>,
    'value' | 'onChange' | 'defaultValue' | 'children' | 'label' | 'labelId' | 'id'
  > {
  label: string;
  value: TriState | undefined;
  onChange: (value: TriState) => void;
  helperText?: ReactNode;
  /** Se true, pulsante X per tornare a `null` (come `FieldOptionDropdown` + `isNullable`). */
  isNullable?: boolean;
  labelId?: string;
  id?: string;
}

/** SI / NO (`'true'`/`'false'`) con reset a `null` (`''`); label al centro se vuoto, come `FieldOptionDropdown`. */
export function TriStateCheckbox({
  label,
  value,
  onChange,
  helperText,
  error,
  required,
  fullWidth = true,
  disabled,
  isNullable = true,
  name,
  id: idProp,
  labelId: labelIdProp,
  variant = 'outlined',
  sx,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  ...selectProps
}: TriStateCheckboxProps) {
  const autoId = useId();
  const [open, setOpen] = useState(false);
  const labelId = labelIdProp ?? `tristate-label-${name ?? autoId}`;
  const selectId = idProp ?? `tristate-select-${name ?? autoId}`;
  const selectValue = toSelectValue(value);
  const hasChoice = selectValue !== '';
  /** Come `FieldOptionDropdown`: senza `displayEmpty` MUI non forza il notch sul vuoto (evita bordo “tagliato” con label al centro). */
  const labelShrunk = hasChoice || open;
  const showClearButton = isNullable && hasChoice && !disabled;

  const handleChange = (event: SelectChangeEvent<TriSelectValue>) => {
    const v = event.target.value as TriSelectValue;
    if (v === '') {
      onChange(null);
    } else if (v === 'true') {
      onChange(true);
    } else {
      onChange(false);
    }
  };

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <FormControl
        fullWidth={fullWidth}
        error={error}
        required={required}
        disabled={disabled}
        sx={sx}
      >
        {label ? (
          <InputLabel id={labelId} htmlFor={selectId} shrink={labelShrunk}>
            {label}
          </InputLabel>
        ) : null}
        <Select<TriSelectValue>
          {...selectProps}
          id={selectId}
          name={name}
          labelId={label ? labelId : undefined}
          label={label}
          value={selectValue}
          variant={variant}
          notched={labelShrunk}
          onOpen={e => {
            onOpenProp?.(e);
            setOpen(true);
          }}
          onClose={e => {
            onCloseProp?.(e);
            setOpen(false);
          }}
          onChange={handleChange}
          endAdornment={
            showClearButton ? (
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  onMouseDown={e => e.preventDefault()}
                  edge="end"
                  aria-label="Azzera selezione"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }
        >
          <MenuItem value="true">SI</MenuItem>
          <MenuItem value="false">NO</MenuItem>
        </Select>
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    </Box>
  );
}
